import React, { MouseEvent, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../services/logger/logger';

//-> Mui components
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

//-> common components
import { useTheme } from '@mui/material/styles';
import { Avatar, Divider, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { setAccessToken, setUserInfo } from '../../store/slices/userSlice';
import MenuIcon from '@mui/icons-material/Menu';
import { EllipsisText } from '../common/styled/EllipsisText';
import { clearAppStorage } from '../../utils/storage';

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const theme = useTheme();
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuIconError, setMenuIconError] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  // Fetch Azure AD profile photo
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      if (accounts.length > 0) {
        try {
          const response = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account: accounts[0],
          });

          const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          });

          if (photoResponse.ok) {
            const blob = await photoResponse.blob();
            const photoUrl = URL.createObjectURL(blob);
            setProfilePhoto(photoUrl);
          }
        } catch (error) {
          // No profile photo available or error fetching - will use initials
          logger.error('Failed to fetch profile photo', error as Error);
        }
      }
    };

    fetchProfilePhoto();

    // Cleanup blob URL on unmount
    return () => {
      if (profilePhoto) {
        URL.revokeObjectURL(profilePhoto);
      }
    };
  }, [accounts, instance, profilePhoto]);

  // Get user's full name from firstName and lastName
  const userFullName = useMemo(() => {
    const firstName = userInfo?.firstName || '';
    const lastName = userInfo?.lastName || '';
    // Fallback to fullUserName if firstName/lastName not available
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return userInfo?.fullUserName || '';
  }, [userInfo?.firstName, userInfo?.lastName, userInfo?.fullUserName]);

  // Get user initials from firstName and lastName
  const userInitials = useMemo(() => {
    const firstName = userInfo?.firstName || '';
    const lastName = userInfo?.lastName || '';

    if (firstName && lastName) {
      return (firstName[0] + lastName[0]).toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (lastName) {
      return lastName[0].toUpperCase();
    }

    // Fallback to fullUserName if firstName/lastName not available
    const fullName = userInfo?.fullUserName || '';
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1 && nameParts[0]) {
      return nameParts[0][0].toUpperCase();
    }

    return 'U';
  }, [userInfo?.firstName, userInfo?.lastName, userInfo?.fullUserName]);

  // Get user role with display name mapping
  const userRole = useMemo(() => {
    // Map backend role keys to display names
    const roleDisplayMap: Record<string, string> = {
      LUBE_ADMIN: t('roles.admin'),
      LUBE_GLOBAL_VIEWER: t('roles.globalViewer'),
      LUBE_EDITOR: t('roles.editor'),
      LUBE_VIEWER: t('roles.viewer'),
    };

    // First check authorityName (primary source based on codebase usage)
    let role = userInfo?.authorityName || '';

    // Fallback to roles/authorities array if authorityName is not present
    if (!role) {
      const roles = userInfo?.roles || userInfo?.authorities || [];
      if (Array.isArray(roles) && roles.length > 0) {
        role =
          typeof roles[0] === 'string'
            ? roles[0]
            : roles[0]?.authorityName || roles[0]?.authority || roles[0]?.role || '';
      }
    }

    // Fallback to role property
    if (!role) {
      role = userInfo?.role || '';
    }

    // Return mapped display name if exists
    if (roleDisplayMap[role]) {
      return roleDisplayMap[role];
    }

    // Fallback formatting for other roles (if any custom roles exist)
    if (role) {
      return role
        .replace(/^ROLE_/i, '')
        .replace(/_/g, ' ')
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }

    return '';
  }, [userInfo, t]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Prevent bubbling to AG Grid row/cell click
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      // Clear Redux state first
      dispatch(setAccessToken(''));
      dispatch(setUserInfo({} as any));

      // Clear all storage owned by this application
      clearAppStorage();

      // Close the menu
      handleClose();

      // Logout from MSAL using redirect (more reliable than popup)
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/login',
      });

      // Note: Code after logoutRedirect won't execute as the page will redirect
    } catch (error) {
      logger.error('Failed to logout', error as Error);

      // Even on error, ensure user is redirected to login
      window.location.href = '/login';
    }
  };

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        backgroundColor: theme.palette.header.background,
        zIndex: theme.zIndex.header,
        padding: '0px 20px',
        height: 58,
      }}
    >
      <Box
        onClick={toggleSidebar}
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        {!menuIconError ? (
          <img
            src="/assets/icons/menu_icon.svg"
            alt="menu"
            onError={() => setMenuIconError(true)}
            style={{ display: 'block', width: '35px', height: '35px' }}
          />
        ) : (
          <MenuIcon
            sx={{
              color: theme.palette.text.primary,
              fontSize: '35px',
            }}
          />
        )}
      </Box>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <Tooltip title={userFullName} arrow placement="bottom">
          <EllipsisText
            variant="body2"
            maxWidth="200px"
            sx={{
              cursor: 'pointer',
              mr: 1,
            }}
          >
            {userFullName}
          </EllipsisText>
        </Tooltip>
        <IconButton sx={{ cursor: 'pointer', p: 0 }} onClick={handleClick}>
          <Avatar
            src={profilePhoto || undefined}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.avatar.bg,
              color: theme.palette.avatar.text,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {userInitials}
          </Avatar>
        </IconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={(e: any) => {
            (e as any)?.stopPropagation?.();
            handleClose();
          }}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
            paper: {
              sx: {
                borderRadius: `${theme.borderRadius.lg}px`,
                boxShadow: theme.customShadows.menu,
                minWidth: '200px',
                mt: 1,
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 20,
                  width: 12,
                  height: 12,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  boxShadow: theme.customShadows.menuArrow,
                },
              },
            },
          }}
          sx={{
            '& .MuiMenu-list': {
              padding: 0,
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* User Info Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 2,
            }}
          >
            <Avatar
              src={profilePhoto || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: theme.palette.avatar.bg,
                color: theme.palette.avatar.text,
                fontSize: '16px',
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip title={userFullName} arrow placement="right">
                <EllipsisText
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: theme.palette.text.primary,
                  }}
                >
                  {userFullName}
                </EllipsisText>
              </Tooltip>
              {userRole && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {userRole}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 0 }} />

          {/* Sign Out Option */}
          <MenuItem
            onClick={(e: any) => {
              e.stopPropagation();
              handleLogout();
            }}
            sx={{
              fontSize: '14px',
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: theme.palette.menuItemHoverBg,
              },
            }}
          >
            {t('buttons.signOut')}
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>
  );
};

export default Header;
